import { Command } from 'commander'
import { createApiClient } from '../http.js'
import { security } from '../api/catalog.js'
import { call } from '../api/client.js'
import { managementScope } from '../context.js'
import { output, outputRecord } from '../output.js'

interface ScopeFlags {
  account?: string
  workspace?: string
  node?: string
  json?: boolean
}

export function registerUser(program: Command): void {
  const userCmd = program
    .command('user')
    .description('Manage users in an account/workspace scope')

  userCmd
    .command('list')
    .description('List users visible in the selected scope')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .option('--search <text>')
    .option('--active <bool>', 'filter by active state (true/false)')
    .option('--json', 'output JSON')
    .action(async (options: ScopeFlags & { search?: string; active?: string }) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      const result = await call<Record<string, unknown>[]>(api, security.listUsers, {
        query: { ...scope, search: options.search, isActive: options.active },
      })
      output(result ?? [], { displayName: 'Name', email: 'Email', isActive: 'Active', userId: 'UserId', id: 'Id' }, options, result)
    })

  userCmd
    .command('get <userId>')
    .description('Show user details')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .option('--json', 'output JSON')
    .action(async (userId: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      const result = await call<Record<string, unknown>>(api, security.getUser, { params: { userId }, query: scope })
      outputRecord(result, options)
    })

  userCmd
    .command('activate <userId>')
    .description('Activate a suspended user in the scope')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (userId: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      await call(api, security.activateUser, { params: { userId }, body: { ...scope, userId } })
      console.log(`User ${userId} activated.`)
    })

  userCmd
    .command('suspend <userId>')
    .description('Suspend a user in the scope')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (userId: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      await call(api, security.suspendUser, { params: { userId }, body: { ...scope, userId } })
      console.log(`User ${userId} suspended.`)
    })

  userCmd
    .command('roles <userId>')
    .description("List a user's role memberships in the scope")
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .option('--json', 'output JSON')
    .action(async (userId: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      const result = await call<Record<string, unknown>[]>(api, security.getUserRoles, { params: { userId }, query: scope })
      output(result ?? [], { roleName: 'Role', roleKey: 'Key', membershipId: 'MembershipId' }, options, result)
    })

  const grantsCmd = userCmd.command('grants').description('Manage direct permission grants for a user')

  grantsCmd
    .command('list <userId>')
    .description('List direct grants for a user')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .option('--json', 'output JSON')
    .action(async (userId: string, options: ScopeFlags) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      const result = await call<Record<string, unknown>[]>(api, security.getUserGrants, { params: { userId }, query: scope })
      output(result ?? [], { scope: 'Scope', resourceKind: 'ResourceKind', resourceId: 'ResourceId', effect: 'Effect', id: 'GrantId' }, options, result)
    })

  grantsCmd
    .command('add <userId> <scope>')
    .description('Grant a permission scope to a user (e.g. node.file.read)')
    .requiredOption('--resource-id <id>', 'resource the grant applies to (account/workspace/node id)')
    .requiredOption('--resource-kind <kind>', 'Account | Workspace | Node')
    .option('--expires <iso8601>', 'expiry timestamp')
    .option('--account <accountId>')
    .option('--workspace <workspaceId>')
    .action(async (userId: string, scopeName: string, options: ScopeFlags & { resourceId: string; resourceKind: string; expires?: string }) => {
      const api = await createApiClient()
      const scope = await managementScope(options)
      await call(api, security.grantUserPermission, {
        params: { userId },
        body: {
          ...scope,
          userId,
          resourceId: options.resourceId,
          resourceKind: options.resourceKind,
          scope: scopeName,
          expiresAt: options.expires ?? null,
        },
      })
      console.log(`Granted ${scopeName} on ${options.resourceKind} ${options.resourceId} to user ${userId}.`)
    })
}
